import { useState, type FormEvent } from 'react';
import { DISPOSAL_METHODS, TRANSITIONS, type DisposalMethod, type LifecycleState } from '@gc/domain';
import type { ApiClient, GadgetRow, MemberRow } from '@gc/api-client';
import { useAction } from '@gc/ui-state';
import { stateLabel, words } from './labels.js';

export type LifecycleActionsApi = {
  lifecycle: Pick<ApiClient['lifecycle'], 'transition' | 'assign' | 'dispose'>;
  gadgets: Pick<ApiClient['gadgets'], 'remove'>;
};

type Panel = 'none' | 'move' | 'assign' | 'dispose' | 'delete';

export function LifecycleActions({ gadget, api, people, onChanged, onDeleted }: {
  gadget: GadgetRow;
  api: LifecycleActionsApi;
  people: readonly MemberRow[];
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const next = TRANSITIONS[gadget.status];
  const moves = next.filter((s) => s !== 'disposed');
  const { busy, failure, run } = useAction();
  const [panel, setPanel] = useState<Panel>('none');
  const [to, setTo] = useState<LifecycleState | ''>('');
  const [reason, setReason] = useState('');
  const [ownerKind, setOwnerKind] = useState<'team' | 'user'>('team');
  const [ownerTeam, setOwnerTeam] = useState(gadget.ownerTeam ?? '');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [location, setLocation] = useState(gadget.location ?? '');
  const [method, setMethod] = useState<DisposalMethod>(DISPOSAL_METHODS[0]);
  const [proceeds, setProceeds] = useState('');
  const [reference, setReference] = useState('');

  const open = (p: Panel) => {
    setPanel(p);
    setReason('');
    setTo(moves[0] ?? '');
    setOwnerUserId(people[0]?.userId ?? '');
  };
  const closeThen = (after: () => void) => (ok: boolean) => { if (ok) { setPanel('none'); after(); } };

  const move = (e: FormEvent) => {
    e.preventDefault();
    if (to === '') return;
    void run(() => api.lifecycle.transition(gadget.id, { to, reason: reason.trim() })).then(closeThen(onChanged));
  };

  const assign = (e: FormEvent) => {
    e.preventDefault();
    void run(() => api.lifecycle.assign(gadget.id, {
      ...(ownerKind === 'team' ? { ownerTeam: ownerTeam.trim() } : { ownerUserId }),
      ...(location.trim() ? { location: location.trim() } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    })).then(closeThen(onChanged));
  };

  const dispose = (e: FormEvent) => {
    e.preventDefault();
    void run(() => api.lifecycle.dispose(gadget.id, {
      method,
      reason: reason.trim(),
      ...(proceeds.trim() ? { proceedsMinor: Math.round(Number(proceeds) * 100) } : {}),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
    })).then(closeThen(onChanged));
  };

  const remove = () => { void run(() => api.gadgets.remove(gadget.id)).then(closeThen(onDeleted)); };

  if (gadget.status === 'disposed') {
    return <p data-testid="lifecycle-closed">Disposed. This record is closed and keeps its history.</p>;
  }

  const cancel = <button type="button" onClick={() => setPanel('none')} disabled={busy}>Cancel</button>;
  const reasonField = (required: boolean) => (
    <label>Reason
      <input required={required} minLength={required ? 3 : undefined} value={reason}
        onChange={(e) => setReason(e.target.value)} data-testid="reason" />
    </label>
  );

  return (
    <div data-testid="lifecycle-actions">
      <div className="toolbar">
        {moves.length > 0 && <button type="button" onClick={() => open('move')} data-testid="action-move">Change state</button>}
        <button type="button" onClick={() => open('assign')} data-testid="action-assign">Assign</button>
        {next.includes('disposed') && <button type="button" onClick={() => open('dispose')} data-testid="action-dispose">Dispose</button>}
        <button type="button" className="danger" onClick={() => open('delete')} data-testid="action-delete">Delete</button>
      </div>
      {failure && <p role="alert" data-testid="lifecycle-failure">{failure}</p>}

      {panel === 'move' && (
        <form className="form" onSubmit={move} data-testid="move-form">
          <div className="grid">
            <label>Move to
              <select value={to} onChange={(e) => setTo(e.target.value as LifecycleState)} data-testid="move-to">
                {moves.map((s) => <option key={s} value={s}>{stateLabel(s)}</option>)}
              </select>
            </label>
            {reasonField(true)}
          </div>
          <div className="form-actions"><button type="submit" className="primary" disabled={busy}>Move</button>{cancel}</div>
        </form>
      )}

      {panel === 'assign' && (
        <form className="form" onSubmit={assign} data-testid="assign-form">
          {people.length > 0 && (
            <div className="toolbar" role="radiogroup" aria-label="Assign to">
              <label className="inline"><input type="radio" name="owner" checked={ownerKind === 'team'} onChange={() => setOwnerKind('team')} />A team</label>
              <label className="inline"><input type="radio" name="owner" checked={ownerKind === 'user'} onChange={() => setOwnerKind('user')} />A person</label>
            </div>
          )}
          <div className="grid">
            {ownerKind === 'team'
              ? <label>Team<input required value={ownerTeam} onChange={(e) => setOwnerTeam(e.target.value)} data-testid="assign-team" /></label>
              : (
                <label>Person
                  <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} data-testid="assign-person">
                    {people.map((p) => <option key={p.userId} value={p.userId}>{p.email}</option>)}
                  </select>
                </label>
              )}
            <label>Location<input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="assign-location" /></label>
            {reasonField(false)}
          </div>
          <div className="form-actions"><button type="submit" className="primary" disabled={busy}>Assign</button>{cancel}</div>
        </form>
      )}

      {panel === 'dispose' && (
        <form className="form" onSubmit={dispose} data-testid="dispose-form">
          <p>Disposal closes the record for good. A gadget that comes back is a new acquisition.</p>
          <div className="grid">
            <label>Method
              <select value={method} onChange={(e) => setMethod(e.target.value as DisposalMethod)} data-testid="dispose-method">
                {DISPOSAL_METHODS.map((m) => <option key={m} value={m}>{words(m)}</option>)}
              </select>
            </label>
            <label>Proceeds (₹)<input type="number" min="0" step="0.01" value={proceeds} onChange={(e) => setProceeds(e.target.value)} /></label>
            <label>Reference<input value={reference} onChange={(e) => setReference(e.target.value)} /></label>
            {reasonField(true)}
          </div>
          <div className="form-actions"><button type="submit" className="primary" disabled={busy}>Dispose</button>{cancel}</div>
        </form>
      )}

      {panel === 'delete' && (
        <div className="form" data-testid="delete-confirm">
          <p>Only a record with no history can be deleted. Anything that has been used is retired and disposed instead.</p>
          <div className="form-actions">
            <button type="button" className="danger" onClick={remove} disabled={busy} data-testid="confirm-delete">Delete {gadget.shortId}</button>
            {cancel}
          </div>
        </div>
      )}
    </div>
  );
}
